<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { formatDuration } from '@/lib/format'
import type { PreviewResponse } from '@/types'

const props = defineProps<{ preview: PreviewResponse }>()

const thumbFailed = ref(false)
const thumbSrc = ref(props.preview.thumbnailUrl)

watch(
  () => props.preview.thumbnailUrl,
  (next) => {
    thumbFailed.value = false
    thumbSrc.value = next
  },
)

/**
 * YouTube does not answer a missing thumbnail with an empty 404 — i.ytimg.com
 * returns a real 120x90 grey placeholder JPEG alongside the 404 status. The
 * browser decodes it and fires `load`, not `error`, so `@error` never sees it
 * and the card silently shows an upscaled grey tile.
 *
 * Only maxresdefault and sddefault are ever missing, and every video ever
 * uploaded has an hqdefault, so a single downgrade is always enough. A URL that
 * is not one of those variants is left alone: `default.jpg` is legitimately
 * 120px wide and must not be mistaken for the sentinel.
 */
const SENTINEL_WIDTH = 120

function onThumbLoad(event: Event) {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > SENTINEL_WIDTH) return

  const downgraded = thumbSrc.value.replace(/\/(?:maxresdefault|sddefault)\.jpg/, '/hqdefault.jpg')
  if (downgraded !== thumbSrc.value) thumbSrc.value = downgraded
}

const duration = computed(() => formatDuration(props.preview.durationSeconds))
</script>

<template>
  <article class="card rise">
    <div class="card__thumb">
      <img
        v-if="!thumbFailed"
        class="card__img"
        :src="thumbSrc"
        alt=""
        decoding="async"
        fetchpriority="high"
        @load="onThumbLoad"
        @error="thumbFailed = true"
      />
      <div v-else class="card__img thumb-fallback" aria-hidden="true" />
      <span class="card__badge">{{ duration }}</span>
    </div>

    <div class="card__body">
      <h2 class="card__title" :title="props.preview.title">{{ props.preview.title }}</h2>

      <p class="card__meta">
        <span class="card__channel">{{ props.preview.channel }}</span>
        <span class="card__meta-dot" aria-hidden="true" />
        <span class="card__format">192 kbps</span>
      </p>

      <div class="card__actions">
        <slot name="actions" />
      </div>
    </div>
  </article>
</template>

<style scoped>
.thumb-fallback {
  background:
    radial-gradient(120% 120% at 20% 0%, rgb(200 250 75 / 14%), transparent 60%),
    repeating-linear-gradient(135deg, var(--bg-2) 0 10px, var(--bg-3) 10px 20px);
}

.card__format {
  flex: none;
  color: var(--text-3);
}
</style>
